import React from 'react';
import { ArrowLeft, Download, PenLine, X } from 'lucide-react';
import { PdfDocumentViewer } from '../../../components/PdfDocumentViewer';
import { SpreadsheetViewer } from '../../../components/SpreadsheetViewer';
import { PdfPrintButton } from '../../../components/PdfPrintButton';
import { PdfMailShareButton } from '../../../components/PdfMailShareButton';
import { spreadsheetPreviewKind } from '../../../utils/spreadsheetPreview';

type PdfPreviewModalProps = {
  open: boolean;
  fileUrl: string | null;
  fileName: string;
  canSendForSignature?: boolean;
  onClose: () => void;
  onSendForSignature?: () => void;
};

export function PdfPreviewModal({
  open,
  fileUrl,
  fileName,
  canSendForSignature = false,
  onClose,
  onSendForSignature,
}: PdfPreviewModalProps) {
  if (!open || !fileUrl) return null;
  const spreadsheetKind = spreadsheetPreviewKind(fileName);
  const title = spreadsheetKind ? 'Tabellen-Vorschau' : 'PDF Vorschau';

  return (
    <div
      className="fixed inset-0 z-[60] flex flex-col bg-app-surface md:items-center md:justify-center md:bg-black/45 md:p-4"
      onClick={onClose}
      role="presentation"
    >
      <div
        className="flex flex-col w-full h-full md:h-[88vh] md:bg-app-surface md:rounded-xl md:border md:border-app-border md:shadow-xl md:max-w-6xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={title}
      >
        <div className="shrink-0 px-4 py-3 border-b border-app-border flex items-center gap-3">
          <button
            type="button"
            className="md:hidden p-1.5 -ml-1 rounded-lg text-app-text-muted hover:bg-app-hover"
            onClick={onClose}
            aria-label="Zurück"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>

          <div className="flex-1 min-w-0">
            <h2 className="text-base font-semibold text-app-text">{title}</h2>
            <p className="text-xs text-app-text-subtle truncate">{fileName}</p>
            {canSendForSignature ? (
              <p className="text-xs text-app-text-subtle mt-1 hidden sm:block">
                Das PDF kann per E-Signatur an einen Empfänger gesendet werden.
              </p>
            ) : null}
          </div>

          <div className="flex items-center gap-2 shrink-0">
            {canSendForSignature && onSendForSignature ? (
              <button
                type="button"
                onClick={onSendForSignature}
                className="inline-flex items-center justify-center gap-1.5 rounded-lg border border-pipedrive-blue bg-app-surface px-3 py-2 text-xs font-medium text-pipedrive-blue hover:bg-blue-50"
              >
                <PenLine className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">Zur Unterschrift senden</span>
                <span className="sm:hidden">Senden</span>
              </button>
            ) : null}
            {!spreadsheetKind ? (
              <>
                <PdfMailShareButton
                  url={fileUrl}
                  filename={fileName}
                  onBeforeOpen={onClose}
                  className="inline-flex items-center justify-center gap-1.5 rounded-lg border border-app-border bg-app-surface px-3 py-2 text-xs font-medium text-app-text-muted hover:bg-app-hover"
                />
                <PdfPrintButton
                  url={fileUrl}
                  className="inline-flex items-center justify-center gap-1.5 rounded-lg border border-app-border bg-app-surface px-3 py-2 text-xs font-medium text-app-text-muted hover:bg-app-hover"
                />
              </>
            ) : null}
            <a
              href={fileUrl}
              download={fileName}
              className="inline-flex items-center justify-center gap-1.5 rounded-lg bg-pipedrive-blue px-3 py-2 text-xs font-medium text-white hover:bg-blue-700"
            >
              <Download className="w-3.5 h-3.5" />
              Herunterladen
            </a>
            <button
              type="button"
              className="hidden md:flex w-8 h-8 rounded-md hover:bg-app-hover text-app-text-subtle items-center justify-center"
              onClick={onClose}
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
        <div className="flex-1 bg-app-surface-muted min-h-0">
          {spreadsheetKind ? (
            <SpreadsheetViewer url={fileUrl} filename={fileName} kind={spreadsheetKind} className="h-full w-full" />
          ) : (
            <PdfDocumentViewer url={fileUrl} title={fileName || 'PDF Vorschau'} className="h-full w-full" />
          )}
        </div>
      </div>
    </div>
  );
}
